import React from "react";
import type { RouteComponentProps } from "react-router-dom";

import { adaptAcaaData } from "@/features/notes/(v2)/(advanced-care)/helpers/adapters";
import { AdvancedCareActivityForm } from "@/features/notes/(v2)/(advanced-care)/components/AdvancedCareActivityForm";
import { cn } from "@/lib/helpers/cn";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { mainStyles } from "@/components/layout/Main";
import { MemberHeader } from "@/features/notes/(v2)/components/MemberHeader";
import type { NoteWithContent } from "@/features/notes/(v2)/(advanced-care)/types";
import type { PatientDetails } from "@/core/types";

// props/state come from ProviderVideoWorkflowStep (still an untyped .js
// class component), so these are narrowed to only the fields this step
// actually reads — not a canonical CallDetails/ProgressNote contract.
type AdvancedCareAssessmentStepProps = {
  props: {
    authRedirectPath: string;
    callDetails?: { patient_username?: string; provider_name?: string };
    history: RouteComponentProps["history"];
  };
  state: {
    patientDetails: PatientDetails;
    // AdvancedCareActivityForm is still untyped, and NoteWithContent's
    // `content` is a loose Record — no canonical ProgressNote shape exists.
    progressNote: NoteWithContent;
  };
};

// Step 2 of the ACP Meeting's two-step guided call ("1. Session" / "2. ACP
// Assessment"). Lives in the video-call portal (not the notes feature) and
// just fetches/renders the shared ACAA form — the draft note itself is
// created in ProviderVideoWorkflowStep.componentDidMount (step 1 of the
// two-step API call) and lives in state.progressNote by the time this step
// is reachable.
export function AdvancedCareAssessmentStep({ props, state }: AdvancedCareAssessmentStepProps) {
  const { patientDetails, progressNote } = state;
  const { authRedirectPath, callDetails, history } = props;

  const { container } = mainStyles();

  return (
    <div className={cn(container(), "tw:flex tw:flex-col tw:gap-5 tw:pt-10 tw:pb-10")}>
      <MemberHeader patientId={patientDetails.username} />

      <DashboardCard2
        className={cn(
          "tw:after:size-screen tw:after:fixed tw:after:inset-0 tw:after:-z-1",
          "tw:after:pointer-events-none tw:after:bg-layer-floor-0",
        )}
        rawChildren
      >
        <AdvancedCareActivityForm
          isEditing
          isEmbedded
          nameToSign={callDetails?.provider_name}
          // progressNote comes straight from the backend (server-prefilled
          // patient_participant_attest, etc., as a raw boolean — see
          // RemoteVideo's handleAcaaNoShow for the no-show case) and needs
          // its yes/no fields mapped to "Yes"/"No" before Formik uses it as
          // initialValues, same as AdvancedCareActivityView does for the
          // view/edit path.
          noteState={adaptAcaaData(progressNote)}
          onSubmitSuccess={() => history.push(authRedirectPath)}
          patientId={callDetails?.patient_username}
        />
      </DashboardCard2>
    </div>
  );
}
